import { IEconomicActivityList } from "../../models/IEconomicActivityList";
import { IFormError, IFormProps } from "../../../utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Interfaces
interface ICreateEconomicActivityListFormFields {
  userId: string;
  name: string;
  cnae: string;
  cnaeSearchTerm: string;
  economicActivities?: string[];
}

interface IEditEconomicActivityListFormFields {
  id: string;
  userId: string;
  name: string;
  cnae?: string;
  cnaeSearchTerm: string;
  economicActivities?: string[];
}

// Interfaces para importação de arquivo
interface IFileData {
  name: string;
  size: string;
  columns: string[];
  type: string;
  data: any[];
}

interface IFileAnalysisData {
  hasEconomicActivityKey: boolean;
  economicActivityKey?: string;
  validValues: string[];
  repeatedList: string[];
  filteredList: string[];
  totalValidCount: number;
  totalRepeatedCount: number;
  totalFilteredCount: number;
}

interface IImportModalState {
  open: boolean;
  type:
    | "success"
    | "error"
    | "warning"
    | "only_repeated"
    | "no_valid_economic_activities"
    | null;
  title: string;
  message: string;
  fileName: string;
  selectedColumn?: string;
  totalImported?: number;
}

// MatchCode interface
interface IMatchCode {
  selectedColumn: string;
  distinctValues: number;
  validValues: number;
}

// FileListData interface
interface IFileListData {
  fileListData: string[];
  fileListTableValues: {
    fileListValidData: string[];
    fileListInvalidData: string[];
    fileListRepeatedData: string[];
  };
}

// Initial States
const createEconomicActivityListInitialState: ICreateEconomicActivityListFormFields =
  {
    userId: "",
    name: "",
    cnae: "",
    cnaeSearchTerm: "",
    economicActivities: undefined,
  };

const editEconomicActivityListInitialState: IEditEconomicActivityListFormFields =
  {
    id: "",
    userId: "",
    name: "",
    cnae: "",
    cnaeSearchTerm: "",
    economicActivities: undefined,
  };

interface IEconomicActivityListState {
  matchCode: IMatchCode;
  fileData: IFileData;
  fileListData: IFileListData;
  // Estados para importação
  fileAnalysis: IFileAnalysisData;
  importModal: IImportModalState;
  selectedColumn: string;
  selectedColumnData: any[];

  openEconomicActivityListDialog: boolean;
  openCreateEconomicActivityListForm: boolean;
  openEditEconomicActivityListForm: boolean;
  openImportListEconomicActivity: boolean;
  economicActivityLists: IEconomicActivityList[];
  selectedEconomicActivityLists: IEconomicActivityList[] | null;
  selectedEconomicActivityList: IEconomicActivityList | null;
  selectedEconomicActivityListToEdit: IEconomicActivityList | null;
  searchTerm?: string;
  createEconomicActivityListForm: IFormProps<ICreateEconomicActivityListFormFields>;
  editEconomicActivityListForm: IFormProps<IEditEconomicActivityListFormFields>;
}

const initialState: IEconomicActivityListState = {
  openCreateEconomicActivityListForm: false,
  openEditEconomicActivityListForm: false,
  openEconomicActivityListDialog: false,
  openImportListEconomicActivity: false,
  economicActivityLists: [],
  selectedEconomicActivityLists: null,
  selectedEconomicActivityList: null,
  selectedEconomicActivityListToEdit: null,
  matchCode: {
    selectedColumn: "",
    distinctValues: 0,
    validValues: 0,
  },
  fileData: {
    name: "",
    size: "",
    columns: [],
    type: "",
    data: [],
  },
  fileListData: {
    fileListData: [],
    fileListTableValues: {
      fileListInvalidData: [],
      fileListRepeatedData: [],
      fileListValidData: [],
    },
  },
  // Estados de importação
  fileAnalysis: {
    hasEconomicActivityKey: false,
    economicActivityKey: "",
    validValues: [],
    repeatedList: [],
    filteredList: [],
    totalValidCount: 0,
    totalRepeatedCount: 0,
    totalFilteredCount: 0,
  },
  importModal: {
    open: false,
    type: null,
    title: "",
    message: "",
    fileName: "",
    selectedColumn: "",
    totalImported: 0,
  },
  selectedColumn: "",
  selectedColumnData: [],

  createEconomicActivityListForm: {
    fields: createEconomicActivityListInitialState,
    errors: [],
    touched: false,
    delivered: false,
  },
  editEconomicActivityListForm: {
    fields: editEconomicActivityListInitialState,
    errors: [],
    touched: false,
    delivered: false,
  },
};

// Validators
export const validateCreateEconomicActivityListForm = (
  fields: ICreateEconomicActivityListFormFields,
): IFormError<keyof ICreateEconomicActivityListFormFields>[] => {
  const errors: IFormError<keyof ICreateEconomicActivityListFormFields>[] = [];

  if (!fields.name?.trim()) {
    errors.push({
      key: "name",
      message: "Nome é obrigatório",
    });
  }

  return errors;
};

export const validateEditEconomicActivityListForm = (
  fields: IEditEconomicActivityListFormFields,
): IFormError<keyof IEditEconomicActivityListFormFields>[] => {
  const errors: IFormError<keyof IEditEconomicActivityListFormFields>[] = [];

  if (!fields.name?.trim()) {
    errors.push({
      key: "name",
      message: "Nome é obrigatório",
    });
  }

  return errors;
};

// Slice
const economicActivityListSlice = createSlice({
  name: "economicActivityList",
  initialState,
  reducers: {
    // set e reset de listas de atividades econômicas
    setEconomicActivityListState(
      state,
      action: PayloadAction<{ economicActivityLists: IEconomicActivityList[] }>,
    ) {
      const economicActivityListsAvailable =
        action.payload.economicActivityLists.filter(
          (list) =>
            !state.selectedEconomicActivityLists?.some(
              (selected) => selected.id === list.id,
            ),
        );
      state.economicActivityLists = economicActivityListsAvailable;
    },

    resetEconomicActivityListState(state) {
      state.economicActivityLists = initialState.economicActivityLists;
      state.selectedEconomicActivityLists =
        initialState.selectedEconomicActivityLists;
    },

    // set de abrir e fechar formulário de lista de atividades econômicas
    setOpenEconomicActivityListDialogState(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.openEconomicActivityListDialog = action.payload;
    },

    setOpenCreateEconomicActivityListFormState(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.openCreateEconomicActivityListForm = action.payload;
    },

    setOpenEconomicActivityListImportFileState(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.openImportListEconomicActivity = action.payload;
    },

    setOpenEditEconomicActivityListFormState(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.openEditEconomicActivityListForm = action.payload;
    },

    // Set dos objetos de criação e edição de lista de atividades econômicas
    setSearchTermState(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },

    // set de campos dos forms create
    setCreateEconomicActivityListNameState(
      state,
      action: PayloadAction<string>,
    ) {
      state.createEconomicActivityListForm.fields.name = action.payload;
    },

    setCreateEconomicActivityListListState(
      state,
      action: PayloadAction<string[]>,
    ) {
      if (!state.selectedEconomicActivityList) return;
      state.selectedEconomicActivityList.economicActivityList = action.payload;
    },

    setCreateEconomicActivityListDataState(
      state,
      action: PayloadAction<string[]>,
    ) {
      // if (!state.selectedEconomicActivityList) return;
      // state.selectedEconomicActivityList. = action.payload;
    },

    setCreateEconomicActivityListCnaeState(
      state,
      action: PayloadAction<string>,
    ) {
      state.createEconomicActivityListForm.fields.cnae = action.payload;
    },

    resetCreateEconomicActivityListCnaeState(state) {
      state.createEconomicActivityListForm.fields.cnae =
        initialState.createEconomicActivityListForm.fields.cnae;
    },

    resetCreateEconomicActivityListState(state) {
      state.createEconomicActivityListForm.fields.economicActivities =
        initialState.createEconomicActivityListForm.fields.economicActivities;
    },

    setAddUnitCnaeState(
      state,
      action: PayloadAction<{ cnae: string; mode: string }>,
    ) {
      const mode: string = action.payload.mode;
      const cnaeUnitario = action.payload.cnae;

      if (mode === "create") {
        state.createEconomicActivityListForm.fields.economicActivities = [
          ...(state.createEconomicActivityListForm.fields.economicActivities ||
            []),
          cnaeUnitario,
        ];
      } else if (mode === "edit") {
        state.editEconomicActivityListForm.fields.economicActivities = [
          ...(state.editEconomicActivityListForm.fields.economicActivities ||
            []),
          cnaeUnitario,
        ];
      }
    },

    setOldCnaeState(
      state,
      action: PayloadAction<{ cnae: string; mode: string }>,
    ) {
      const mode: string = action.payload.mode;
      if (mode === "create") {
        state.createEconomicActivityListForm.fields.cnae = action.payload.cnae;
      } else if (mode === "edit") {
        state.editEconomicActivityListForm.fields.cnae = action.payload.cnae;
      }
    },

    setNewCnaeState(
      state,
      action: PayloadAction<{ cnae: string; mode: string }>,
    ) {
      const mode: string = action.payload.mode;
      if (mode === "create") {
        state.createEconomicActivityListForm.fields.cnae = action.payload.cnae;
      } else if (mode === "edit") {
        state.editEconomicActivityListForm.fields.cnae = action.payload.cnae;
      }
    },

    // Reset states para importação
    resetEconomicActivityListFileDataState(state) {
      state.fileData = initialState.fileData;
      state.fileAnalysis = initialState.fileAnalysis;
      state.importModal = initialState.importModal;
      state.selectedColumn = initialState.selectedColumn;
      state.selectedColumnData = initialState.selectedColumnData;
      state.matchCode = initialState.matchCode;
    },

    resetEconomicActivityListSelectedColumnState(state) {
      state.selectedColumn = initialState.selectedColumn;
      state.selectedColumnData = initialState.selectedColumnData;
      state.matchCode.selectedColumn = initialState.matchCode.selectedColumn;
      state.matchCode.distinctValues = initialState.matchCode.distinctValues;
      state.matchCode.validValues = initialState.matchCode.validValues;
    },

    resetEconomicActivityListFileAnalysisState(state) {
      state.fileAnalysis = initialState.fileAnalysis;
      state.matchCode.distinctValues = initialState.matchCode.distinctValues;
      state.matchCode.validValues = initialState.matchCode.validValues;
    },

    resetEconomicActivityListImportModalState(state) {
      state.importModal = initialState.importModal;
    },

    // edit
    setSelectEconomicActivityListToEditState(
      state,
      action: PayloadAction<IEconomicActivityList>,
    ) {
      state.editEconomicActivityListForm.fields.id = action.payload.id;
      state.editEconomicActivityListForm.fields.userId =
        action.payload.createdBy;
      state.editEconomicActivityListForm.fields.name = action.payload.name;
      state.editEconomicActivityListForm.fields.economicActivities =
        action.payload.economicActivityList;
    },

    setEditEconomicActivityListNameState(state, action: PayloadAction<string>) {
      //if (!state.selectedEconomicActivityList) return;
      state.editEconomicActivityListForm.fields.name = action.payload;
    },

    setEditEconomicActivityListState(state, action: PayloadAction<string[]>) {
      if (!state.selectedEconomicActivityList) return;
      state.selectedEconomicActivityList.economicActivityList = action.payload;
    },

    // set de campos dos forms edit

    setEditEconomicActivityListDataState(
      state,
      action: PayloadAction<string[]>,
    ) {
      if (!state.selectedEconomicActivityList) return;
      state.selectedEconomicActivityList.economicActivityList = action.payload;
    },

    setEditEconomicActivityListCnaeState(state, action: PayloadAction<string>) {
      state.editEconomicActivityListForm.fields.cnae = action.payload;
    },

    resetEditEconomicActivityListNameState(state) {
      state.editEconomicActivityListForm.fields.name =
        initialState.editEconomicActivityListForm.fields.name;
    },

    resetEditEconomicActivityListCnaeState(state) {
      state.editEconomicActivityListForm.fields.cnae =
        initialState.editEconomicActivityListForm.fields.cnae;
    },

    resetEditEconomicActivityListState(state) {
      state.editEconomicActivityListForm.fields.economicActivities =
        initialState.editEconomicActivityListForm.fields.economicActivities;
    },

    setUpdateStatusEconomicActivityListState(
      state,
      action: PayloadAction<{ id: string; active: boolean }>,
    ) {
      // Encontra o índice da lista com o ID correspondente
      const listIndex = state.economicActivityLists.findIndex(
        (list) => list.id === action.payload.id,
      );

      // Se encontrou a lista, atualiza o status active
      if (listIndex !== -1) {
        state.economicActivityLists[listIndex].active = action.payload.active;
      }

      // Também verifica se a lista está selecionada e atualiza lá também
      if (state.selectedEconomicActivityLists) {
        const selectedIndex = state.selectedEconomicActivityLists.findIndex(
          (list) => list.id === action.payload.id,
        );

        if (selectedIndex !== -1) {
          state.selectedEconomicActivityLists[selectedIndex].active =
            action.payload.active;
        }
      }

      // Se for a lista que está sendo editada atualmente, atualiza também
      if (state.selectedEconomicActivityListToEdit?.id === action.payload.id) {
        state.selectedEconomicActivityListToEdit.active = action.payload.active;
      }

      // Se for a lista selecionada atualmente, atualiza também
      if (state.selectedEconomicActivityList?.id === action.payload.id) {
        state.selectedEconomicActivityList.active = action.payload.active;
      }

      // REORDENA a lista disponível após atualizar o status
      state.economicActivityLists = [...state.economicActivityLists].sort(
        (a, b) => {
          const activeA = a.active ?? false;
          const activeB = b.active ?? false;

          if (activeA !== activeB) {
            return activeA ? -1 : 1;
          }

          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();

          return nameA.localeCompare(nameB, "pt", { sensitivity: "base" });
        },
      );
    },

    // outras funções
    setUpdateCnaeInListState(
      state,
      action: PayloadAction<{
        oldValue: string;
        newValue: string;
        index: number;
        mode: string;
      }>,
    ) {
      const mode: string = action.payload.mode;
      const { oldValue, newValue, index } = action.payload;

      if (mode === "create") {
        // Verifica se existe a lista de atividades econômicas
        if (!state.createEconomicActivityListForm.fields.economicActivities) {
          return;
        }

        // Cria uma cópia da lista atual
        const updatedList = [
          ...state.createEconomicActivityListForm.fields.economicActivities,
        ];

        // Atualiza o item no índice específico
        if (index >= 0 && index < updatedList.length) {
          updatedList[index] = newValue;

          // Atualiza o estado com a nova lista
          state.createEconomicActivityListForm.fields.economicActivities =
            updatedList;
        }
      } else if (mode === "edit") {
        // Verifica se existe a lista de atividades econômicas
        if (!state.editEconomicActivityListForm.fields.economicActivities) {
          return;
        }

        // Cria uma cópia da lista atual
        const updatedList = [
          ...state.editEconomicActivityListForm.fields.economicActivities,
        ];

        // Atualiza o item no índice específico
        if (index >= 0 && index < updatedList.length) {
          updatedList[index] = newValue;

          // Atualiza o estado com a nova lista
          state.editEconomicActivityListForm.fields.economicActivities =
            updatedList;
        }
      }
    },

    setRemoveCnaeFromListState(
      state,
      action: PayloadAction<{
        value: string;
        index: number;
        mode: string;
      }>,
    ) {
      const mode: string = action.payload.mode;
      const list = action.payload;

      if (mode === "create") {
        const currentList = [
          ...(state.createEconomicActivityListForm.fields.economicActivities ||
            []),
        ];
        currentList.splice(list.index, 1);
        state.createEconomicActivityListForm.fields.economicActivities =
          currentList;
      } else if (mode === "edit") {
        const currentList = [
          ...(state.editEconomicActivityListForm.fields.economicActivities ||
            []),
        ];
        currentList.splice(list.index, 1);
        state.editEconomicActivityListForm.fields.economicActivities =
          currentList;
      }
    },

    setSelectEconomicActivityListState(
      state,
      action: PayloadAction<{
        selectedEconomicActivityList: IEconomicActivityList;
      }>,
    ) {
      const list = action.payload.selectedEconomicActivityList;

      // Adiciona aos selecionados
      state.selectedEconomicActivityLists = [
        ...(state.selectedEconomicActivityLists || []),
        list,
      ];

      // Remove da lista disponível
      state.economicActivityLists = state.economicActivityLists.filter(
        (economicActivityList) => economicActivityList.id !== list.id,
      );

      // Reordena a lista disponível
      state.economicActivityLists = [...state.economicActivityLists].sort(
        (a, b) => {
          const activeA = a.active ?? false;
          const activeB = b.active ?? false;

          if (activeA !== activeB) {
            return activeA ? -1 : 1;
          }

          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();

          return nameA.localeCompare(nameB, "pt", { sensitivity: "base" });
        },
      );
    },

    setUnselectEconimicActivityListState(
      state,
      action: PayloadAction<IEconomicActivityList>,
    ) {
      const list = action.payload;

      // Adiciona à lista disponível
      state.economicActivityLists = [...state.economicActivityLists, list];

      // Remove dos selecionados
      state.selectedEconomicActivityLists =
        state.selectedEconomicActivityLists?.filter(
          (selected) => selected.id !== list.id,
        ) || null;

      // Reordena a lista disponível
      state.economicActivityLists = [...state.economicActivityLists].sort(
        (a, b) => {
          const activeA = a.active ?? false;
          const activeB = b.active ?? false;

          if (activeA !== activeB) {
            return activeA ? -1 : 1;
          }

          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();

          return nameA.localeCompare(nameB, "pt", { sensitivity: "base" });
        },
      );
    },

    setSelectedEconomicActivityListState(
      state,
      action: PayloadAction<IEconomicActivityList>,
    ) {
      state.selectedEconomicActivityList = action.payload;
    },

    setSelectAllEconomicActivityListsState(state) {
      // const validLists =
      //   state.selectedEconomicActivityLists?.filter(
      //     (list) => list.active === true,
      //   ) || [];

      // const invalidLists =
      //   state.selectedEconomicActivityLists?.filter(
      //     (list) => list.active === false,
      //   ) || [];

      // state.selectedEconomicActivityLists = [...validLists];
      // state.economicActivityLists = [...invalidLists];


      // NÃO IMPLEMENTADO
    },

    // ordenar lista

    setSortEconomicActivityListState(
      state,
      action: PayloadAction<{ economicActivityLists: IEconomicActivityList[] }>,
    ) {
      // Pega a lista atual e apenas ordena, sem filtrar
      const sortedLists = [...action.payload.economicActivityLists].sort(
        (a, b) => {
          // Garantir que active tenha um valor padrão
          const activeA = a.active ?? false;
          const activeB = b.active ?? false;

          // Ordenar por active (true primeiro)
          if (activeA !== activeB) {
            return activeA ? -1 : 1;
          }

          // Ordenar por nome (case-insensitive)
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();

          return nameA.localeCompare(nameB, "pt", { sensitivity: "base" });
        },
      );

      state.economicActivityLists = sortedLists;
    },

    // REDUCERS PARA IMPORTAÇÃO DE ARQUIVO
    setEconomicActivityListFileDataState(
      state,
      action: PayloadAction<{ fileData: IFileData }>,
    ) {
      state.fileData = action.payload.fileData;
    },

    setEconomicActivityListFileAnalysisDataState(
      state,
      action: PayloadAction<IFileAnalysisData>,
    ) {
      state.fileAnalysis = action.payload;
    },

    setEconomicActivityListImportModalState(
      state,
      action: PayloadAction<Partial<IImportModalState>>,
    ) {
      state.importModal = {
        ...state.importModal,
        ...action.payload,
      };
    },

    setSelectedEconomicActivityColumnState(
      state,
      action: PayloadAction<{ selectedColumn: string; data: any[] }>,
    ) {
      state.selectedColumn = action.payload.selectedColumn;
      state.selectedColumnData = action.payload.data;

      // Atualiza o matchCode
      state.matchCode.selectedColumn = action.payload.selectedColumn;

      // Calcula valores distintos
      const distinctValues = new Set(
        action.payload.data.map((row) =>
          String(row[action.payload.selectedColumn] || ""),
        ),
      );
      state.matchCode.distinctValues = distinctValues.size;
    },

    setAddMultipleEconomicActivitiesToListState(
      state,
      action: PayloadAction<{ economicActivities: string[]; mode: string }>,
    ) {
      const mode: string = action.payload.mode;

      if (mode == "create") {
        const currentList =
          state.createEconomicActivityListForm.fields.economicActivities || [];

        // Remove duplicatas antes de adicionar
        const newActivities = action.payload.economicActivities.filter(
          (activity) => !currentList.includes(activity),
        );

        state.createEconomicActivityListForm.fields.economicActivities = [
          ...currentList,
          ...newActivities,
        ];
        // Atualiza o matchCode com a contagem de valores válidos
        state.matchCode.validValues = newActivities.length;
      } else if (mode === "edit") {
        const currentList =
          state.editEconomicActivityListForm.fields.economicActivities || [];

        // Remove duplicatas antes de adicionar
        const newActivities = action.payload.economicActivities.filter(
          (activity) => !currentList.includes(activity),
        );

        state.editEconomicActivityListForm.fields.economicActivities = [
          ...currentList,
          ...newActivities,
        ];
        // Atualiza o matchCode com a contagem de valores válidos
        state.matchCode.validValues = newActivities.length;
      }
    },

    // Novo reducer para limpar todos os dados de importação
    resetAllImportDataState(state) {
      state.fileData = initialState.fileData;
      state.fileAnalysis = initialState.fileAnalysis;
      state.importModal = initialState.importModal;
      state.selectedColumn = initialState.selectedColumn;
      state.selectedColumnData = initialState.selectedColumnData;
      state.matchCode = initialState.matchCode;
    },
  },
});

export const {
  setEconomicActivityListState,
  setSelectedEconomicActivityListState,
  setSortEconomicActivityListState,
  resetEconomicActivityListState,

  // OPEN FORM
  setOpenEconomicActivityListDialogState,
  setOpenEconomicActivityListImportFileState,
  setOpenCreateEconomicActivityListFormState,
  setOpenEditEconomicActivityListFormState,

  setSearchTermState,
  setSelectEconomicActivityListState,
  setUnselectEconimicActivityListState,
  setSelectAllEconomicActivityListsState,

  setUpdateCnaeInListState,
  setRemoveCnaeFromListState,

  // CREATE
  setCreateEconomicActivityListDataState,
  setCreateEconomicActivityListListState,
  setCreateEconomicActivityListNameState,
  setCreateEconomicActivityListCnaeState,
  resetCreateEconomicActivityListState,

  setAddUnitCnaeState,
  resetCreateEconomicActivityListCnaeState,
  setNewCnaeState,
  setOldCnaeState,

  // EDIT
  setSelectEconomicActivityListToEditState,
  setEditEconomicActivityListDataState,
  setEditEconomicActivityListState,
  setEditEconomicActivityListNameState,
  setEditEconomicActivityListCnaeState,
  resetEditEconomicActivityListNameState,
  resetEditEconomicActivityListCnaeState,
  resetEditEconomicActivityListState,
  setUpdateStatusEconomicActivityListState,

  // IMPORTAÇÃO DE ARQUIVO
  setEconomicActivityListFileDataState,
  setEconomicActivityListFileAnalysisDataState,
  setEconomicActivityListImportModalState,
  setSelectedEconomicActivityColumnState,
  setAddMultipleEconomicActivitiesToListState,

  // RESETS DE IMPORTAÇÃO
  resetEconomicActivityListFileDataState,
  resetEconomicActivityListFileAnalysisState,
  resetEconomicActivityListSelectedColumnState,
  resetEconomicActivityListImportModalState,
  resetAllImportDataState,
} = economicActivityListSlice.actions;

export const economicActivityListReducer = economicActivityListSlice.reducer;
